/**
 * Dex screen layouts: where Pokémon GO draws its Pokédex on each screen shape.
 *
 * Every dex ROI is a fraction of the captured frame, and those fractions depend
 * on the SHAPE of the screen, not its size: GO lays the grid out by width, so a
 * 4:3 iPad shows wider cards that cover more of its (relatively shorter)
 * height, a taller header card, and the variant pills higher up the screen.
 *
 * PHONE is the 19.5:9 / 20:9 calibration the scanner has always used
 * (886x1918 and 1179x2556 iPhone frames, the 923x2000 fixtures, ~576x1280
 * Galaxy frames). Its numbers are the historic dexOcrRoi / pokedexOcr
 * constants, byte for byte (dexLayout.test.ts pins that), and it is the
 * default of every geometry function, so nothing moves for a phone frame.
 *
 * TABLET is measured from the owner's iPad screenshots of 2026-10-23
 * (1500x2000, 4:3: a main-dex page under the expanded stats header, a Lucky
 * page under the compact header, an XXL page with the quick-scroll pill, and a
 * Palkia appraisal). The three grid shots agree to a pixel:
 *   cards at x 0.070-0.277 / 0.290-0.492 / 0.505-0.707 / 0.720-0.927
 *   (pitch 0.217 of the width), row pitch 0.217 of the height (card 0.209 +
 *   gutter 0.008), header card 0.072-0.281 expanded and to 0.152 compact, the
 *   variant pills 0.748-0.826, the round buttons 0.856-0.946, status bar 0.02.
 * Two to three rows fit between the header and the pills (four on a phone).
 *
 * `dexLayoutForFrame` picks by aspect (lib/frameGeometry). Shapes outside
 * both bands keep the phone layout and carry the `uncalibrated_frame_aspect`
 * warning.
 */
import { classifyFrameAspect } from "./frameGeometry";

export interface LayoutRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DexScreenLayout {
  id: "phone" | "tablet";
  /** Horizontal extent the columns are tiled across. */
  gridLeft: number;
  gridRight: number;
  /** 4-column row pitch as a fraction of the frame height. */
  rowHeight: number;
  /** Bottom of the lattice: a row ending just above the variant pills. */
  gridBottom: number;
  /** Tightest expanded-header bottom; rows whose top is above it are dropped. */
  headerMaxBottom: number;
  /** Row 0 is not read while its top is above this (header / HUD clearance). */
  topRowClearY: number;
  /** Variant pill band: top and height, the detector's fallback. */
  pillRowY: number;
  pillRowH: number;
  /** Where the pill-row detector looks, fractions of the height. */
  pillSearch: { top: number; bottom: number };
  /** Row-pitch window for the grid autocorrelation, fractions of the height. */
  gridPitchSearch: { min: number; max: number };
  /** Bands OCR text must never be sourced from. */
  zones: {
    statusBar: LayoutRect;
    header: LayoutRect;
    selector: LayoutRect;
    pip: LayoutRect;
  };
  /** Header text crops. */
  headerRois: {
    regionLabel: LayoutRect;
    filterRow: LayoutRect;
    count: LayoutRect;
    statsRow: LayoutRect;
    tabStrip: LayoutRect;
  };
  /** Centre of the legacy top tab strip, or null where the screen has none. */
  tabBarY: number | null;
}

export const PHONE_LAYOUT: DexScreenLayout = {
  id: "phone",
  gridLeft: 0.02,
  gridRight: 0.98,
  rowHeight: 0.14625,
  gridBottom: 0.79,
  headerMaxBottom: 0.205,
  topRowClearY: 0.25,
  pillRowY: 0.85,
  pillRowH: 0.07,
  pillSearch: { top: 0.8, bottom: 0.9 },
  gridPitchSearch: { min: 0.08, max: 0.16 },
  zones: {
    statusBar: { x: 0, y: 0, w: 1, h: 0.055 },
    header: { x: 0, y: 0.055, w: 1, h: 0.15 },
    selector: { x: 0, y: 0.86, w: 1, h: 0.14 },
    pip: { x: 0.5, y: 0.055, w: 0.5, h: 0.135 },
  },
  headerRois: {
    regionLabel: { x: 0.02, y: 0.06, w: 0.55, h: 0.08 },
    filterRow: { x: 0, y: 0.06, w: 1, h: 0.06 },
    count: { x: 0.3, y: 0.06, w: 0.4, h: 0.07 },
    statsRow: { x: 0, y: 0.12, w: 1, h: 0.06 },
    tabStrip: { x: 0, y: 0, w: 1, h: 0.06 },
  },
  tabBarY: 0.03,
};

export const TABLET_LAYOUT: DexScreenLayout = {
  id: "tablet",
  gridLeft: 0.07,
  gridRight: 0.927,
  rowHeight: 0.217,
  gridBottom: 0.731,
  headerMaxBottom: 0.281,
  // Just under the expanded header: the iPad shows two rows there and losing
  // the top one would halve each frame. The phone's extra clearance was for a
  // HUD card parked top-right; the HUD detector handles that per frame.
  topRowClearY: 0.29,
  pillRowY: 0.748,
  pillRowH: 0.078,
  pillSearch: { top: 0.72, bottom: 0.85 },
  gridPitchSearch: { min: 0.15, max: 0.28 },
  zones: {
    statusBar: { x: 0, y: 0, w: 1, h: 0.02 },
    header: { x: 0, y: 0.02, w: 1, h: 0.261 },
    selector: { x: 0, y: 0.74, w: 1, h: 0.26 },
    pip: { x: 0.5, y: 0.02, w: 0.5, h: 0.135 },
  },
  headerRois: {
    // "KANTO" sits at x 0.07-0.19, y 0.095-0.131 under both header states;
    // the count pill at x 0.36-0.64, y 0.089-0.135; the stats row (SHINY /
    // LUCKY / XXL / XXS / 100%) at y 0.175-0.245 when expanded.
    regionLabel: { x: 0.04, y: 0.08, w: 0.5, h: 0.07 },
    filterRow: { x: 0, y: 0.08, w: 1, h: 0.06 },
    count: { x: 0.3, y: 0.08, w: 0.4, h: 0.07 },
    statsRow: { x: 0.02, y: 0.165, w: 0.96, h: 0.09 },
    // No tab strip on the iPad: the band is the status bar, read for parity.
    tabStrip: { x: 0, y: 0, w: 1, h: 0.03 },
  },
  tabBarY: null,
};

/** The layout for a frame of this size: tablet for 4:3-class shapes, else phone. */
export function dexLayoutForFrame(width: number, height: number): DexScreenLayout {
  return classifyFrameAspect(width, height) === "tablet" ? TABLET_LAYOUT : PHONE_LAYOUT;
}
